// Shared components & helpers per la Social Page (split di SocialPage.jsx).
// Contiene: PLATFORMS registry, formatNumber, PlatformPill, KpiCard,
// ClientPickerButton, PeriodPicker, computePeriodDates, isWeekend.
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { Instagram, Facebook, Youtube, Calendar as CalIcon, ChevronsUpDown, Check, ChevronLeft, ChevronRight } from "lucide-react";

import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";

export const PLATFORMS = [
  { key: "instagram", label: "Instagram", color: "#E1306C", gradient: "from-fuchsia-500 to-orange-400", textHex: "text-pink-600", border: "border-pink-300", bg: "bg-pink-50", icon: Instagram },
  { key: "facebook", label: "Facebook", color: "#1877F2", gradient: "from-blue-600 to-blue-400", textHex: "text-blue-600", border: "border-blue-300", bg: "bg-blue-50", icon: Facebook },
  { key: "youtube", label: "YouTube", color: "#FF0000", gradient: "from-red-600 to-red-400", textHex: "text-red-600", border: "border-red-300", bg: "bg-red-50", icon: Youtube },
  { key: "tiktok", label: "TikTok", color: "#000000", gradient: "from-slate-900 to-slate-600", textHex: "text-slate-900", border: "border-slate-400", bg: "bg-slate-50", icon: null },
];
export const PLATFORM_BY_KEY = Object.fromEntries(PLATFORMS.map(p => [p.key, p]));

export function formatNumber(n) {
  if (n == null) return "-";
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "k";
  return Math.round(n).toLocaleString("it-IT");
}

export function PlatformPill({ platform, size = "sm" }) {
  const meta = PLATFORM_BY_KEY[platform];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs text-white px-2 py-0.5 rounded-md bg-gradient-to-br ${meta.gradient}`} style={{ fontSize: size === "xs" ? 10 : 12 }}>
      {Icon ? <Icon className="w-3 h-3" /> : <span className="font-bold text-[10px]">TT</span>}
      {meta.label}
    </span>
  );
}

export function KpiCard({ label, value, icon, testid }) {
  return (
    <Card className="p-3" data-testid={testid}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-bold mt-1 font-mono">{typeof value === "number" ? formatNumber(value) : value}</div>
    </Card>
  );
}

export function ClientPickerButton({ clients, value, onChange, testid = "client-picker", filterFn }) {
  const [open, setOpen] = useState(false);
  const list = filterFn ? clients.filter(filterFn) : clients;
  const selected = list.find(c => c.client_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testid} className="w-[260px] justify-between">
          <span className="truncate">{selected ? selected.name : "Seleziona cliente"}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Cerca cliente..." data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>Nessun cliente</CommandEmpty>
            <CommandGroup>
              {list.map(c => (
                <CommandItem key={c.client_id} value={`${c.name} ${c.client_id}`} onSelect={() => { onChange(c.client_id); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === c.client_id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const PERIOD_PRESETS = [
  { v: "7d", l: "Ultimi 7 giorni" },
  { v: "30d", l: "Ultimi 30 giorni" },
  { v: "60d", l: "Ultimi 60 giorni" },
  { v: "90d", l: "Ultimi 90 giorni" },
  { v: "6m", l: "Ultimi 6 mesi" },
  { v: "12m", l: "Ultimi 12 mesi" },
];

export function computePeriodDates(preset) {
  if (!preset || preset === "all" || preset === "__all__") {
    return { from: null, to: null };
  }
  // Custom range: preset è nel formato "custom:YYYY-MM-DD:YYYY-MM-DD"
  if (typeof preset === "string" && preset.startsWith("custom:")) {
    const [, f, t] = preset.split(":");
    if (f && t) return { from: f, to: t };
  }
  const now = new Date();
  const to = format(now, "yyyy-MM-dd");
  let from;
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "7d": from = format(new Date(now.getTime() - 7 * 86400000), "yyyy-MM-dd"); break;
    case "30d": from = format(new Date(now.getTime() - 30 * 86400000), "yyyy-MM-dd"); break;
    case "60d": from = format(new Date(now.getTime() - 60 * 86400000), "yyyy-MM-dd"); break;
    case "90d": from = format(new Date(now.getTime() - 90 * 86400000), "yyyy-MM-dd"); break;
    case "6m": from = format(new Date(y, m - 6, now.getDate()), "yyyy-MM-dd"); break;
    case "12m": from = format(new Date(y - 1, m, now.getDate()), "yyyy-MM-dd"); break;
    default: from = format(new Date(now.getTime() - 30 * 86400000), "yyyy-MM-dd");
  }
  return { from, to };
}

function formatRangeItalian(fromIso, toIso) {
  if (!fromIso || !toIso) return "Tutte le date";
  try {
    const dFrom = new Date(fromIso + "T12:00:00");
    const dTo = new Date(toIso + "T12:00:00");
    const strFrom = dFrom.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
    const strTo = dTo.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
    return `${strFrom} → ${strTo}`;
  } catch (_) {
    return `${fromIso} → ${toIso}`;
  }
}

function ItalianCalendarGrid({ from, to, onSelectRange }) {
  const oggiIso = format(new Date(), "yyyy-MM-dd");
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = to ? new Date(to + "T12:00:00") : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectingStart, setSelectingStart] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const monthName = currentMonth.toLocaleDateString("it-IT", { month: "long" });
  const monthLabel = `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;

  // Monday is 0: (day + 6) % 7
  const firstDayIndex = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const handleDayClick = (isoStr) => {
    if (!selectingStart) {
      setSelectingStart(isoStr);
      onSelectRange(isoStr, isoStr);
    } else {
      if (isoStr < selectingStart) {
        onSelectRange(isoStr, selectingStart);
      } else {
        onSelectRange(selectingStart, isoStr);
      }
      setSelectingStart(null);
      setHoverDate(null);
    }
  };

  const activeStart = selectingStart || from;
  const activeEnd = selectingStart
    ? (hoverDate || selectingStart)
    : to;

  const rangeStart = activeStart && activeEnd && activeStart > activeEnd ? activeEnd : activeStart;
  const rangeEnd = activeStart && activeEnd && activeStart > activeEnd ? activeStart : activeEnd;

  const dayCells = [];
  // Prev month filler
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    const dNum = daysInPrevMonth - i;
    dayCells.push({ day: dNum, isCurrentMonth: false, iso: "" });
  }
  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    dayCells.push({ day: d, isCurrentMonth: true, iso });
  }
  // Next month filler
  const remaining = (7 - (dayCells.length % 7)) % 7;
  for (let d = 1; d <= remaining; d++) {
    dayCells.push({ day: d, isCurrentMonth: false, iso: "" });
  }

  return (
    <div className="select-none py-1">
      {/* Month Navigation */}
      <div className="flex items-center justify-between px-1 mb-2">
        <button
          type="button"
          onClick={prevMonth}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F1EDE5] text-[#1C1A17] transition-colors cursor-pointer"
          title="Mese precedente"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-[12.5px] font-semibold tracking-tight capitalize" style={{ color: "#1C1A17" }}>
          {monthLabel}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F1EDE5] text-[#1C1A17] transition-colors cursor-pointer"
          title="Mese successivo"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
        {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => (
          <span key={i} className="text-[10px] font-medium" style={{ color: "#8A857C" }}>
            {d}
          </span>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-y-1 gap-x-0 text-center">
        {dayCells.map((cell, idx) => {
          if (!cell.isCurrentMonth) {
            return (
              <div key={idx} className="h-7 flex items-center justify-center text-[11px] text-[#A39D93] opacity-25">
                {cell.day}
              </div>
            );
          }

          const isStart = cell.iso === rangeStart;
          const isEnd = cell.iso === rangeEnd;
          const isBetween = rangeStart && rangeEnd && cell.iso > rangeStart && cell.iso < rangeEnd;
          const isToday = cell.iso === oggiIso;

          let bg = "transparent";
          let color = "#1C1A17";
          let rounded = "rounded-lg";

          if (isStart && isEnd) {
            bg = "#B4532C";
            color = "#FFF";
            rounded = "rounded-lg";
          } else if (isStart) {
            bg = "#B4532C";
            color = "#FFF";
            rounded = "rounded-l-lg rounded-r-none";
          } else if (isEnd) {
            bg = "#B4532C";
            color = "#FFF";
            rounded = "rounded-r-lg rounded-l-none";
          } else if (isBetween) {
            bg = "#FAF0E2";
            color = "#B4532C";
            rounded = "rounded-none";
          }

          return (
            <button
              key={idx}
              type="button"
              onClick={() => handleDayClick(cell.iso)}
              onMouseEnter={() => { if (selectingStart) setHoverDate(cell.iso); }}
              className={`h-7 relative flex items-center justify-center text-[11.5px] transition-colors cursor-pointer ${rounded}`}
              style={{
                background: bg,
                color: color,
                fontWeight: isStart || isEnd ? 600 : isBetween ? 500 : 400,
              }}
            >
              <span>{cell.day}</span>
              {isToday && !isStart && !isEnd && (
                <span
                  className="absolute bottom-0.5 w-1 h-1 rounded-full"
                  style={{ background: isBetween ? "#B4532C" : "#B4532C" }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PeriodPicker({ value, onChange, allowAll = false, testid = "period-picker" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("presets"); // "presets" | "calendar"

  const isAll = !value || value === "all" || value === "__all__";
  const isCustom = typeof value === "string" && value.startsWith("custom:");
  const current = isAll
    ? { v: "", l: "Tutte le date" }
    : isCustom
      ? { v: value, l: "Personalizzato" }
      : (PERIOD_PRESETS.find(p => p.v === value) || PERIOD_PRESETS[1]);

  const defaultDates = useMemo(() => computePeriodDates("30d"), []);
  const { from, to } = computePeriodDates(value);

  const [tempFrom, setTempFrom] = useState(from || defaultDates.from);
  const [tempTo, setTempTo] = useState(to || defaultDates.to);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setTempFrom(from || defaultDates.from);
      setTempTo(to || defaultDates.to);
      setTab(isCustom ? "calendar" : "presets");
    }
    setOpen(nextOpen);
  };

  const applyCustomRange = () => {
    if (tempFrom && tempTo) {
      const f = tempFrom <= tempTo ? tempFrom : tempTo;
      const t = tempFrom <= tempTo ? tempTo : tempFrom;
      onChange(`custom:${f}:${t}`);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className="inline-flex items-center">
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="group flex items-center justify-between gap-2 h-9 px-3 rounded-[10px] text-[13px] font-medium transition-all duration-150 cursor-pointer shadow-sm hover:border-[#DCD4C6] focus:outline-none"
            style={{
              background: "#FFFEFC",
              border: `1px solid ${!isAll ? "#B4532C" : open ? "#B4532C" : "#E7E2D9"}`,
              color: !isAll ? "#B4532C" : "#57534B",
            }}
          >
            <div className="flex items-center gap-2 truncate">
              <CalIcon className="w-3.5 h-3.5 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" style={{ color: !isAll ? "#B4532C" : "#8A857C" }} />
              <span className="truncate max-w-[180px]">
                {isAll ? "Tutte le date" : isCustom ? formatRangeItalian(from, to) : current.l}
              </span>
            </div>
            {!isAll && (
              <span
                role="button"
                title="Azzera date"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-[#F3E3D1] transition-colors ml-1"
                style={{ color: "#B4532C" }}
              >
                <Check className="hidden" />
                <span className="text-[11px] leading-none">✕</span>
              </span>
            )}
            {isAll && (
              <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 opacity-40 group-hover:opacity-80 transition-opacity ml-1" style={{ color: "#8A857C" }} />
            )}
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="w-[300px] p-3 rounded-2xl shadow-2xl border z-50 animate-in fade-in-0 zoom-in-95"
        align="start"
        sideOffset={6}
        style={{ background: "#FFFEFC", borderColor: "#E7E2D9", color: "#1C1A17" }}
      >
        {/* Tab switch */}
        <div className="flex p-0.5 rounded-xl mb-3 border" style={{ background: "#F7F4EF", borderColor: "#EDE9E1" }}>
          <button
            type="button"
            onClick={() => setTab("presets")}
            className="flex-1 py-1 text-[11.5px] font-medium rounded-lg transition-all cursor-pointer"
            style={{
              background: tab === "presets" ? "#FFFEFC" : "transparent",
              color: tab === "presets" ? "#1C1A17" : "#8A857C",
              boxShadow: tab === "presets" ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
            }}
          >
            Periodi rapidi
          </button>
          <button
            type="button"
            onClick={() => setTab("calendar")}
            className="flex-1 py-1 text-[11.5px] font-medium rounded-lg transition-all cursor-pointer"
            style={{
              background: tab === "calendar" ? "#FFFEFC" : "transparent",
              color: tab === "calendar" ? "#1C1A17" : "#8A857C",
              boxShadow: tab === "calendar" ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
            }}
          >
            Calendario
          </button>
        </div>

        {tab === "presets" ? (
          <div className="space-y-0.5">
            {allowAll && (
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                className="w-full text-left px-2.5 py-2 rounded-xl text-[12.5px] transition-colors flex items-center justify-between cursor-pointer mb-1 border-b"
                style={{
                  background: isAll ? "#FAF0E2" : "transparent",
                  color: isAll ? "#B4532C" : "#1C1A17",
                  borderColor: "#EDE9E1",
                  fontWeight: isAll ? 600 : 400,
                }}
                onMouseEnter={(e) => { if (!isAll) e.currentTarget.style.background = "#F7F4EF"; }}
                onMouseLeave={(e) => { if (!isAll) e.currentTarget.style.background = "transparent"; }}
                data-testid="period-opt-all"
              >
                <div className="flex items-center gap-2">
                  <Check className={`w-3.5 h-3.5 shrink-0 ${isAll ? "opacity-100" : "opacity-0"}`} style={{ color: "#B4532C" }} />
                  <span>Tutte le date</span>
                </div>
              </button>
            )}

            {PERIOD_PRESETS.map((p) => {
              const isSelected = value === p.v;
              return (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => { onChange(p.v); setOpen(false); }}
                  className="w-full text-left px-2.5 py-2 rounded-xl text-[12.5px] transition-colors flex items-center justify-between cursor-pointer"
                  style={{
                    background: isSelected ? "#FAF0E2" : "transparent",
                    color: isSelected ? "#B4532C" : "#1C1A17",
                    fontWeight: isSelected ? 600 : 400,
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#F7F4EF"; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                  data-testid={`period-opt-${p.v}`}
                >
                  <div className="flex items-center gap-2">
                    <Check className={`w-3.5 h-3.5 shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`} style={{ color: "#B4532C" }} />
                    <span>{p.l}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div>
            <ItalianCalendarGrid
              from={tempFrom}
              to={tempTo}
              onSelectRange={(f, t) => { setTempFrom(f); setTempTo(t); }}
            />
            <div className="mt-3 pt-2.5 border-t" style={{ borderColor: "#EDE9E1" }}>
              <div className="flex items-center justify-between text-[11px] mb-2 px-1">
                <span style={{ color: "#8A857C" }}>Selezionato:</span>
                <span className="font-semibold font-mono" style={{ color: "#B4532C" }}>
                  {tempFrom && tempTo ? formatRangeItalian(tempFrom, tempTo) : "Seleziona date"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const today = format(new Date(), "yyyy-MM-dd");
                    setTempFrom(today);
                    setTempTo(today);
                  }}
                  className="px-2.5 h-8 text-[11.5px] font-medium rounded-xl border hover:bg-[#F7F4EF] transition-colors cursor-pointer"
                  style={{ borderColor: "#E7E2D9", color: "#57534B" }}
                >
                  Oggi
                </button>
                <button
                  type="button"
                  onClick={applyCustomRange}
                  disabled={!tempFrom || !tempTo}
                  className="flex-1 h-8 text-white text-[12px] font-medium rounded-xl transition-all shadow-sm cursor-pointer disabled:opacity-50"
                  style={{ background: "#B4532C" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#984220"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#B4532C"; }}
                  data-testid={`${testid}-custom-apply`}
                >
                  Applica intervallo
                </button>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function isWeekend(d) { const g = d.getDay(); return g === 0 || g === 6; }
